import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ScrollLock, { TouchScrollable } from "react-scrolllock"; // TODO: WP-1418 - replace by body-scroll-lock package
import environment from "src/environment";
import { useAlgoliaQuery, useProductsSearch } from "src/hooks";
import { i18n } from "src/i18n";
import { getHeader } from "src/redux/header";
import styled from "styled-components";

import { Hyperlink, ToggleSlide } from "..";
import { translate } from "../../services/translation.service";
import theme, { media } from "../../theme";
import { IProduct } from "../../types/product";
import Price from "../Price";
import { IStep } from "../ProgressBar/VerticalProgressBar/VerticalProgressBar.component";

import InstantSearchList from "./InstantSearchList";

interface IHits {
  path: string;
  url: string;
  product_count: number;
}

export interface IProps {
  input: string;
  className?: string;
  show: boolean;
  hide: () => void;
  positionTop?: number | null;
}

interface IWrapperProps {
  headerHeight: number;
  show?: boolean;
  positionTop?: number | null;
}

interface IContentWrapper extends IWrapperProps {
  windowHeight: number;
}

const ContentWrapper = styled.section<IContentWrapper>`
  height: ${({ windowHeight, headerHeight }: IContentWrapper): number =>
    windowHeight - (headerHeight || 0) + 12}px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${media.laptop} {
    height: initial;
  }
`;

const StyledInstantSearchList = styled(InstantSearchList)`
  align-items: flex-start;
  min-height: min-content;
  margin: 0;
`;

const SubLabel = styled.div`
  margin: 0;
  font-size: 15px;
  font-weight: normal;
  color: ${theme.colors.grey};
`;

const Body = styled.section`
  background-color: ${theme.colors.white};
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 ${theme.margins.paddingPageBig};
  overflow: scroll;
  margin-bottom: -1px;

  ${media.laptop} {
    overflow: initial;
  }
`;

const Wrapper = styled.article<IWrapperProps>`
  display: ${({ show }: IWrapperProps): string => (show ? "flex" : "none")};
  flex-direction: column;
  position: fixed;
  top: ${({ headerHeight }: IWrapperProps): string => `${headerHeight - 12}px`};
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;

  ${media.laptop} {
    top: ${({ positionTop }: IWrapperProps): number => positionTop ?? 0}px;
    bottom: initial;
    max-height: 600px;
    position: absolute;
    margin: auto;
    overflow-y: auto;
    box-shadow: 4px 10px 30px rgba(0, 0, 0, 0.5);
  }
`;

const NoProducts = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
  width: 100%;
  padding: ${theme.margins.paddingPage} 0;
  color: ${theme.colors.grey};
`;

const StyledHyperlink = styled(Hyperlink)`
  border-bottom: solid 1px
    ${({ isLast }: { isLast: boolean }): string =>
      isLast ? theme.colors.grey : theme.colors.veryLightGrey};
  padding: ${theme.margins.paddingPage} 0;
`;

const mapProductDetailsToSteps = (products: IProduct[]): IStep[] => {
  return products.map(
    (product: IProduct): IStep => ({
      imageSrc: product.image,
      label: product.name,
      productUrl: product.url,
      subLabel: (
        <SubLabel>
          {product.displayPrice && (
            <Price
              actualPrice={product.actualPrice}
              originalPrice={product.originalPrice}
            />
          )}
        </SubLabel>
      ),
    })
  );
};

const InstantSearch = (props: IProps): JSX.Element | null => {
  const { show, hide, input, className, positionTop } = props;
  const [innerHeight, setInnerHeight] = useState(window.innerHeight);
  const [toggleSlide, setToggleSlide] = useState(false);
  const [open, setOpen] = useState(false);
  const windowResize = (): void => setInnerHeight(window.innerHeight);

  const header = useSelector(getHeader);

  const { language } = i18n;

  const { hits: desktopCategory } = useAlgoliaQuery<IHits>(
    {
      run: show,
      query: input,
      indexName: `${environment.ALGOLIA.ENV}_b2c_${language}_${language}_categories`,
      hitsPerPage: 2,
      numericFilters: ["include_in_menu=1"],
      params: {
        analyticsTags: ["autocomplete"],
      },
    },
    [input, language]
  );

  /**
   * Query products
   */
  const { query, loading } = useProductsSearch({
    run: show,
    input,
    hitsPerPage: 10,
  });

  const products = query?.data?.products ?? [];

  useEffect(() => {
    if (show && !open && !toggleSlide) {
      setOpen(true);
    } else if (!show && open) {
      setOpen(false);
    }
  }, [show, open, toggleSlide]);

  useEffect(() => {
    window.addEventListener("resize", windowResize);

    return (): void => {
      window.removeEventListener("resize", windowResize);
    };
  }, []);

  const onRest = (): void => {
    if (!open && show) {
      setToggleSlide(false);
      hide();
    }
  };

  const onClick = (): void => {
    setToggleSlide(true);
    setOpen(false);
  };

  return (
    <Wrapper
      headerHeight={
        header.height + (header.isSticky ? 0 : header.topVisibleOffset)
      }
      show={show}
      className={className}
      positionTop={positionTop}
    >
      <ScrollLock isActive={open} />
      <ToggleSlide open={open} onRest={onRest} duration={200}>
        <ContentWrapper
          headerHeight={
            header.height + (header.isSticky ? 0 : header.topVisibleOffset)
          }
          windowHeight={innerHeight}
        >
          <TouchScrollable>
            <Body>
              {desktopCategory.map((hit: IHits, i: number) => (
                <StyledHyperlink
                  key={`cat-link-${i}`}
                  isLast={i === desktopCategory.length - 1}
                  to={URL ? new URL(hit.url).pathname : hit.url}
                  onClick={onClick}
                >
                  {hit.path} ({hit.product_count})
                </StyledHyperlink>
              ))}
              {loading || products?.length > 0 ? (
                <StyledInstantSearchList
                  imageWidth={68}
                  onClick={onClick}
                  steps={mapProductDetailsToSteps(products)}
                />
              ) : (
                <NoProducts>
                  <p style={{ margin: 0 }}>
                    {translate("product.noProductFound")}
                  </p>
                </NoProducts>
              )}
            </Body>
          </TouchScrollable>
        </ContentWrapper>
      </ToggleSlide>
    </Wrapper>
  );
};

export default InstantSearch;
